import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { BadgeCheck, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { reviews, type Review } from '../data/site';
import { Reveal } from './Reveal';
const AUTOPLAY_MS = 6000;
const CLAMP_LENGTH = 240;
function usePerView() {
  const [perView, setPerView] = useState(1);
  useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      setPerView(width >= 1280 ? 3 : width >= 768 ? 2 : 1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return perView;
}
function ReviewCard({
  review
}: {
  review: Review;
}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > CLAMP_LENGTH;
  const body = isLong && !expanded ? `${review.text.slice(0, CLAMP_LENGTH).trimEnd()}…` : review.text;
  return <article className="flex h-full flex-col rounded-2xl border border-sand-dark/40 bg-white p-7 shadow-soft sm:p-8" data-id="element-250" data-mp="3jzpbgrw11v9b">
      <header className="flex items-start justify-between gap-4" data-id="element-251" data-mp="2raazif6557z7">
        <div className="flex items-center gap-3.5" data-id="element-252" data-mp="2mcswf5et1mz4">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-sand-light font-serif text-lg text-ocean" aria-hidden="true" data-id="element-253" data-mp="2he7y5bj45ddb">
            {review.name.charAt(0)}
          </span>
          <div data-id="element-254" data-mp="3a2nd5y28vvon">
            <p className="text-[15px] font-semibold text-forest-deep" data-id="element-255" data-mp="3a59hlotu9v2n">
              {review.name}
            </p>
            <p className="mt-0.5 text-[13px] text-forest-deep/55" data-id="element-256" data-mp="356esk55a3aa3">
              <span aria-hidden="true" data-id="element-257" data-mp="30bsoyo5fum0k">{review.flag}</span> {review.country}
            </p>
          </div>
        </div>

        <span className="flex-shrink-0 rounded-xl bg-ocean px-3 py-2 text-center" data-id="element-258" data-mp="2w9nu5m397jcc">
          <span className="block font-serif text-lg leading-none text-ivory" data-id="element-259" data-mp="3suxxcap4d4xf">
            {review.score}
          </span>
          <span className="mt-1 block text-[9px] uppercase tracking-[0.14em] text-ivory/60" data-id="element-260" data-mp="3o13gdf0wf421">
            / 10
          </span>
        </span>
      </header>

      <h3 className="mt-6 font-serif text-xl leading-snug text-forest-deep" data-id="element-261" data-mp="1r35wjq3d3xhl">
        {review.title}
      </h3>

      <p className="mt-3 flex-1 text-[15px] leading-[1.75] text-forest-deep/70" data-id="element-262" data-mp="1cf1kh4ytek7s">
        {body}
        {isLong && <button type="button" onClick={() => setExpanded(value => !value)} className="ml-1.5 whitespace-nowrap text-[13px] font-medium text-ocean underline-offset-4 transition-colors hover:underline" data-id="element-263" data-mp="1hckyq4viiwzq">
            {expanded ? 'Read less' : 'Read more'}
          </button>}
      </p>

      {review.negative && <p className="mt-4 border-l-2 border-sand-dark pl-3.5 text-[14px] leading-relaxed text-forest-deep/50" data-id="element-264" data-mp="ygl8ayi6o1i9">
          {review.negative}
        </p>}

      <footer className="mt-7 flex items-center justify-between gap-3 border-t border-sand-dark/40 pt-5" data-id="element-265" data-mp="2sucnedja7dl3">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-forest-deep/50" data-id="element-266" data-mp="h5xy8u5zowyh">
          <BadgeCheck className="h-3.5 w-3.5 text-gold" aria-hidden="true" data-id="element-267" data-mp="c8ph8wwkuyyt" />
          Verified · Booking.com
        </span>
        <time className="text-[12px] text-forest-deep/40" data-id="element-268" data-mp="qxloirqt87yd">{review.date}</time>
      </footer>
    </article>;
}
export function Reviews() {
  const perView = usePerView();
  const [index, setIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);
  const maxIndex = Math.max(0, reviews.length - perView);
  useEffect(() => {
    setIndex(value => Math.min(value, maxIndex));
  }, [maxIndex]);
  const goTo = useCallback((next: number, manual = true) => {
    if (manual) setAutoplay(false);
    setIndex(Math.min(Math.max(next, 0), maxIndex));
  }, [maxIndex]);
  useEffect(() => {
    if (!autoplay || maxIndex === 0) return;
    const timer = window.setInterval(() => {
      setIndex(value => value >= maxIndex ? 0 : value + 1);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [autoplay, maxIndex]);
  return <section id="reviews" aria-label="Guest reviews" className="w-full bg-sand-light py-24 sm:py-28 lg:py-36" onMouseEnter={() => setAutoplay(false)} data-id="element-269" data-mp="3d5i1nbiftj2j">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12" data-id="element-270" data-mp="3n138po2d59j5">
        <Reveal className="mx-auto max-w-2xl text-center" data-id="element-271" data-mp="2pf5kda1ay9za">
          <h2 className="font-serif text-[clamp(2rem,4.5vw,3.5rem)] font-light leading-[1.08] text-forest-deep" data-id="element-272" data-mp="2ud75e0iagjde">
            What our guests say
          </h2>
          <p className="mt-4 text-[16px] leading-relaxed text-forest-deep/60" data-id="element-273" data-mp="2zapbzk8u2mi8">
            Real experiences from guests who stayed with us
          </p>
        </Reveal>

        <Reveal delay={0.08} data-id="element-274" data-mp="26oeckcwyqrn0">
          <div className="relative mt-14 lg:mt-16" data-id="element-275" data-mp="26ncrdfm8gork">
            <div className="overflow-hidden px-0 lg:px-16" data-id="element-276" data-mp="2bm64v81isfrq">
              <motion.div ref={trackRef} className="flex items-stretch" animate={{
              x: `-${index * (100 / perView)}%`
            }} transition={{
              duration: 0.45,
              ease: [0.22, 1, 0.36, 1]
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.08} onDragEnd={(_, info) => {
              if (info.offset.x < -60) goTo(index + 1);
              if (info.offset.x > 60) goTo(index - 1);
            }} data-id="element-277" data-mp="2bklapjj4ayue">
                {reviews.map(review => <div key={review.name + review.date} className="w-full flex-shrink-0 px-2.5 md:w-1/2 xl:w-1/3" data-id="element-278" data-mp="4zwa16s7e3t8">
                    <ReviewCard review={review} data-id="element-279" data-mp="3s89qyeiav8o1" />
                  </div>)}
              </motion.div>
            </div>

            <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Previous reviews" className="absolute -left-1 top-1/2 z-10 -translate-y-1/2 rounded-full border border-sand-dark/60 bg-ivory/90 p-2.5 text-forest-deep/70 backdrop-blur-sm transition-all duration-300 ease-calm hover:scale-105 hover:bg-ivory hover:text-forest-deep disabled:pointer-events-none disabled:opacity-30 lg:left-0 lg:p-3.5" data-id="element-280" data-mp="3n9gb4ffjj5kp">
              <ChevronLeft className="h-4 w-4 lg:h-5 lg:w-5" strokeWidth={1.25} aria-hidden="true" data-id="element-281" data-mp="3rbobcwzggd3x" />
            </button>

            <button type="button" onClick={() => goTo(index + 1)} disabled={index === maxIndex} aria-label="Next reviews" className="absolute -right-1 top-1/2 z-10 -translate-y-1/2 rounded-full border border-sand-dark/60 bg-ivory/90 p-2.5 text-forest-deep/70 backdrop-blur-sm transition-all duration-300 ease-calm hover:scale-105 hover:bg-ivory hover:text-forest-deep disabled:pointer-events-none disabled:opacity-30 lg:right-0 lg:p-3.5" data-id="element-282" data-mp="3ij2slgvtq6f3">
              <ChevronRight className="h-4 w-4 lg:h-5 lg:w-5" strokeWidth={1.25} aria-hidden="true" data-id="element-283" data-mp="1ynglqt747qod" />
            </button>
          </div>
        </Reveal>

        <div className="mt-8 flex items-center justify-center gap-2" data-id="element-284" data-mp="28drmy7zezlrk">
          {Array.from({
          length: maxIndex + 1
        }).map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Go to review ${i + 1}`} aria-current={i === index} className={`h-1.5 rounded-full transition-all duration-300 ease-calm ${i === index ? 'w-5 bg-forest-deep/45' : 'w-1.5 bg-forest-deep/20 hover:bg-forest-deep/35'}`} data-id="element-285" data-mp="1kp8ffuo6vd0j" data-mpi={i} />)}
        </div>

        <div className="mt-10 flex flex-col items-center gap-2" data-id="element-286" data-mp="310vvgmxpui83">
          <div className="flex gap-1" aria-hidden="true" data-id="element-287" data-mp="yfi6yoqit0hr">
            {Array.from({
            length: 5
          }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" data-id="element-288" data-mp="13elceaaex8q9" data-mpi={i} />)}
          </div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-forest-deep/45" data-id="element-289" data-mp="18c4q99377jho">
            Verified guest reviews · Booking.com
          </p>
        </div>
      </div>
    </section>;
}